"use client";

import { useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { WAITLIST_QUERY_KEYS } from "@punchup/data/client";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@punchup/data/legacy/hooks";

/**
 * Hook that automatically joins a waitlist after authentication redirect
 * Detects waitlist query parameters and executes the join action
 */
export function useAutoJoinWaitlist() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const user = useUser();
  const hasProcessed = useRef(false);

  useEffect(() => {
    const shouldJoinWaitlist = searchParams.get("joinWaitlist");
    const showId = searchParams.get("showId");
    const showName = searchParams.get("showName");
    const ownerId = searchParams.get("ownerId");
    const isVenue = searchParams.get("isVenue");

    // Only proceed if we have all required params, user is authenticated, and haven't processed yet
    if (
      !shouldJoinWaitlist ||
      !showId ||
      !ownerId ||
      !user ||
      hasProcessed.current
    ) {
      return;
    }

    hasProcessed.current = true;

    const joinWaitlist = async () => {
      try {
        await getApiClient().post("/api/v2/user/waitlist", {
          showName: showName || "Show",
          receiveUpdates: true,
          showId,
          ownerId,
          isVenue: isVenue === "true",
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [WAITLIST_QUERY_KEYS.perShow],
            refetchType: "active",
          }),
          queryClient.invalidateQueries({
            queryKey: [WAITLIST_QUERY_KEYS.batched],
            refetchType: "active",
          }),
          queryClient.invalidateQueries({
            queryKey: [WAITLIST_QUERY_KEYS.dashboard],
          }),
        ]);

        // Show success toast
        toast({
          title: "You're on the waitlist! 🎉",
          description:
            "We'll notify you when tickets become available for this show.",
          variant: "success",
        });

        // Remove the waitlist params from URL
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete("joinWaitlist");
        newSearchParams.delete("showId");
        newSearchParams.delete("showName");
        newSearchParams.delete("ownerId");
        newSearchParams.delete("isVenue");

        const newUrl = newSearchParams.toString()
          ? `?${newSearchParams.toString()}`
          : window.location.pathname;
        router.replace(newUrl);
      } catch (error) {
        logException(error);
        toast({
          title: "Failed to join waitlist",
          description: "Please try again.",
          variant: "destructive",
        });

        // Still clean up the URL even on error
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete("joinWaitlist");
        newSearchParams.delete("showId");
        newSearchParams.delete("showName");
        newSearchParams.delete("ownerId");
        newSearchParams.delete("isVenue");

        const newUrl = newSearchParams.toString()
          ? `?${newSearchParams.toString()}`
          : window.location.pathname;
        router.replace(newUrl);
      }
    };

    joinWaitlist();
  }, [searchParams, user, router, queryClient]);
}
